import { useAgentCopy } from '../../lib/agentCopy.js';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardSummary, PlatformNotification } from '@worki/shared';
import { api, billingApi } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { useRealtime } from '../../lib/useRealtime.js';
import { AgentShellEntry } from '../AgentIntro.js';
import { ErrorBoundary } from '../ErrorBoundary.js';
import { BottomNav } from './BottomNav.js';
import { DesktopSidebar } from './DesktopSidebar.js';
import { TopBar, type ShellNotification } from './TopBar.js';
import { pageTitleKey } from './navItems.js';
import { useNavigationMode } from '../../lib/navigationLayout.js';

interface NotificationsPayload {
  items: ShellNotification[];
  unread: number;
}

/**
 * ADM-004 — where each kind of platform notification is dealt with.
 *
 * The bell is a way in, not a report: a pending approval is answered on the
 * approvals screen and nowhere else, so that is where the line goes.
 */
const PLATFORM_LINKS: Record<PlatformNotification['kind'], string> = {
  pendingApproval: '/platform/approvals',
  pastDue: '/platform/businesses',
  trialEnding: '/platform/businesses',
  subscriptionEnding: '/platform/businesses',
  cancelled: '/platform/businesses',
  dormant: '/platform/businesses',
  newLead: '/platform/leads',
};

export function AppLayout() {
  const navigationMode = useNavigationMode();
  const { t } = useTranslation();
  const agentCopy = useAgentCopy();
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  useRealtime(Boolean(session));

  /**
   * AUTH-009 — whether the API will still accept a write from this business.
   * The same `['billing']` query the billing screen uses, so the shell costs no
   * extra request once that screen has been opened.
   */
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => billingApi.summary(),
    enabled: Boolean(session),
  });

  // Only once the answer has arrived: announcing a lockout while the summary is
  // still in flight would flash the banner at every account on every reload.
  const readOnly = billing.data ? !billing.data.writable : false;

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationsPayload>('/notifications'),
    enabled: Boolean(session),
  });

  /*
   * ADM-004 — inside the back office the bell is the platform's, not the
   * employee's own business's.
   *
   * A member of Worki's staff has an account like everyone else, and while they
   * are looking across every business their own quotes and reminders are not
   * what the bell is for: an account waiting three days for approval is, and it
   * appeared nowhere until someone opened the right screen. Outside `/platform`
   * the bell is the ordinary one again — this swaps which inbox is shown, and
   * never mixes the two.
   */
  const inPlatform = pathname.startsWith('/platform');
  const platformInbox = useQuery({
    queryKey: ['platform', 'notifications'],
    queryFn: () => api.get<{ items: PlatformNotification[] }>('/platform/notifications'),
    // The route answers 404 to anyone who is not staff, so asking is the same
    // question as "may I", and it is only asked where the answer is used.
    enabled: Boolean(session) && inPlatform,
    retry: false,
  });

  /*
   * The platform inbox is computed from the accounts rather than stored, so it
   * has no read state and no id of its own. The key has to be stable across
   * refetches — React would otherwise remount every row on each poll — so it is
   * built from what the entry is about rather than from its position.
   */
  const platformItems: ShellNotification[] = (platformInbox.data?.items ?? []).map((item) => ({
    id: `${item.kind}:${item.businessId ?? item.name}:${item.at ?? ''}`,
    // Reused rather than given a type of its own: `NotificationType` names the
    // events a business records, and nothing in the bell reads the field.
    type: 'reminder_due',
    title: `admin.notifications.bell.${item.kind}`,
    body: null,
    link: PLATFORM_LINKS[item.kind],
    readAt: null,
    createdAt: item.at ?? new Date().toISOString(),
    vars: { name: item.name },
  }));

  // DASH-002 — the reminders badge reads from the same summary the widget uses.
  const summary = useQuery({
    queryKey: ['dashboard', 'badge'],
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary?range=all'),
    enabled: Boolean(session),
  });
  const openReminders = summary.data?.openReminderCount ?? 0;

  /**
   * The unread count is served twice — the bell reads `/notifications`, the
   * dashboard widget reads `dashboard/summary` — so both have to be refreshed or
   * the badge and the widget disagree about how many are left.
   */
  const refreshUnread = () => {
    void notifications.refetch();
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSettled: refreshUnread,
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSettled: refreshUnread,
  });

  const handleLogout = () => {
    void logout().then(() => navigate('/login', { replace: true }));
  };

  return (
    <div className="flex min-h-dvh w-full">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:z-[200] focus:m-2 focus:rounded-lg focus:bg-card focus:px-3 focus:py-2 focus:shadow-lg"
        href="#main-content"
      >
        {t('nav.dashboard')}
      </a>

      {navigationMode === 'sidebar' && <DesktopSidebar onLogout={handleLogout} />}

      {/*
        A real height, not a minimum. `min-h-screen` let this column grow to fit
        whatever the page rendered, so `main` — which is the app's scroll
        container — never overflowed itself and the window scrolled instead.
        Anything positioned `sticky` inside a screen was then anchored to a box
        taller than the viewport and never stuck to anything; the quote editor's
        running total was the visible symptom, sliding off the bottom of the
        screen. `dvh` and not `vh` because a phone's address bar shrinks the
        viewport as it scrolls.
      */}
      <div className="flex-1 flex flex-col h-dvh overflow-hidden">
        <TopBar
          title={pathname === '/agent' ? agentCopy('title') : t(pathname === '/notifications' ? 'notifications.title' : pageTitleKey(pathname))}
          items={inPlatform ? platformItems : (notifications.data?.items ?? [])}
          unread={inPlatform ? platformItems.length : (notifications.data?.unread ?? 0)}
          // The employee's own reminders are their business's, not the
          // platform's, and counting them here would put a badge on the back
          // office for work that is waiting somewhere else entirely.
          openReminders={inPlatform ? 0 : openReminders}
          readable={!inPlatform}
          {...(inPlatform ? { viewAllTo: '/platform/notifications' } : { viewAllTo: '/notifications', activityTo: '/notifications?tab=activities' })}
          onMarkAllRead={() => markAllRead.mutate()}
          onOpenNotification={(notification) => {
            if (!inPlatform && !notification.readAt) markRead.mutate(notification.id);
          }}
        />

        <main id="main-content" className="page-container min-h-0" style={navigationMode === 'bottom' ? { paddingBottom: '7rem' } : undefined}>
          {/*
            Without this, a read-only account is a mystery: every button on every
            screen still renders and each one fails into a toast that is gone a
            second later. `status` and not `alert`: a standing condition rather
            than news. The account screen prints the same sentence inside its
            plan card, so the banner steps aside there.
          */}
          {readOnly && pathname !== '/profile' && (
            <div
              role="status"
              className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm"
            >
              <span className="flex-1 min-w-0">
                {t(
                  billing.data?.subscription.status === 'past_due'
                    ? 'billing.pastDueNotice'
                    : 'billing.inactiveNotice',
                )}
              </span>
              <Link to="/profile" className="font-medium text-primary hover:underline">
                {t('nav.billing')}
              </Link>
            </div>
          )}

          {/* A page that throws takes only itself down; the chrome stays on
              screen. `resetKey` is the pathname, so leaving clears the error. */}
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <AgentShellEntry />
      <BottomNav desktop={navigationMode === 'bottom'} />
    </div>
  );
}
